import { memo, ReactNode, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Utils
import { getStatusTooltipPosition, StatusTooltipPosition } from './utils';
import { combineClasses } from '@shared/utils';
interface CustomTooltipProps {
  children: ReactNode;
  content: string;
  boundaryRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  arrowClassName?: string;
}
const PORTAL_ARROW_CLASSNAME = 'absolute top-full w-0 h-0 -translate-x-1/2 border-4 border-frost-900 border-x-transparent border-b-transparent';
export const CustomTooltip = memo(({
  children,
  content,
  boundaryRef,
  className
}: CustomTooltipProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const portalTooltipRef = useRef<HTMLDivElement>(null);
  const isRestoringRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<StatusTooltipPosition | null>(null);
  const handleOpen = useCallback(() => {
    if (isRestoringRef.current) return;
    setIsOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTooltipPosition(null);
  }, []);
  const updateTooltipPosition = useCallback(() => {
    if (typeof window === 'undefined' || !triggerRef.current || !portalTooltipRef.current) {
      return;
    }
    const nextTooltipPosition = getStatusTooltipPosition({
      triggerRect: triggerRef.current.getBoundingClientRect(),
      tooltipRect: portalTooltipRef.current.getBoundingClientRect(),
      boundaryRect: boundaryRef?.current?.getBoundingClientRect(),
      viewportWidth: window.innerWidth
    });
    setTooltipPosition(currentTooltipPosition => {
      if (currentTooltipPosition && currentTooltipPosition.left === nextTooltipPosition.left && currentTooltipPosition.top === nextTooltipPosition.top && currentTooltipPosition.arrowLeft === nextTooltipPosition.arrowLeft) {
        return currentTooltipPosition;
      }
      return nextTooltipPosition;
    });
  }, [boundaryRef]);
  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    updateTooltipPosition();
  }, [isOpen, updateTooltipPosition]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        isRestoringRef.current = true;
        handleClose();
        if (triggerRef.current && triggerRef.current.contains(document.activeElement)) {
          (document.activeElement as HTMLElement)?.blur();
        }
      } else {
        // Clear the guard after the next frame so that any
        // focus-restoration events fired by the browser are suppressed
        requestAnimationFrame(() => {
          isRestoringRef.current = false;
        });
      }
    };
    const handleWindowBlur = () => {
      isRestoringRef.current = true;
      handleClose();
    };
    const handleWindowFocus = () => {
      requestAnimationFrame(() => {
        isRestoringRef.current = false;
      });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [handleClose]);
  return <>
        <div ref={triggerRef} data-testid="tooltip" className="relative outline-none" tabIndex={-1} onMouseEnter={handleOpen} onMouseLeave={handleClose} onFocus={handleOpen} onBlur={handleClose}>
          {children}
        </div>

        {/* Only render the tooltip when isOpen is true and document is defined to prevent rendering in SSR */}
        {isOpen && typeof document !== 'undefined' && createPortal(<div data-testid="tooltip-portal-content" ref={portalTooltipRef} style={{
      top: tooltipPosition?.top ?? 0,
      left: tooltipPosition?.left ?? 0,
      visibility: tooltipPosition ? 'visible' : 'hidden'
    }} className={combineClasses('pointer-events-none fixed z-50 w-max rounded-xs bg-frost-900 px-2.5 py-1 text-[11px] text-white', className)}>
              {content}
              <span data-testid="tooltip-portal-arrow" style={{
        left: tooltipPosition?.arrowLeft ?? 0
      }} className={PORTAL_ARROW_CLASSNAME} />
            </div>, document.body)}
      </>;
});